<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Request Number -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('complaints.filterByRequestNumber')"
                v-model.trim="filterOptions.request_number"
              />
              <!-- End:: Request Number -->

              <!-- Start:: Delivery Agent Name -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('walletRequests.deliverySearchPlaceholder')"
                v-model.trim="filterOptions.name"
              />
              <!-- End:: Delivery Agent Name -->

              <!-- Start:: Status Select -->
              <base-select-input
                col="4"
                :optionsList="statusOptions"
                :placeholder="$t('walletRequests.filterByStatus')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Select -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("walletRequests.deliveryWalletTitle") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('walletRequests.loadingRequests')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("walletRequests.noRequests") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serialNumber`]="{ item }">
          <p>{{ item.serialNumber }}</p>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Request ID -->
        <template v-slot:[`item.id`]="{ item }">
          <p>#{{ item.id }}</p>
        </template>
        <!-- End:: Request ID -->

        <!-- Start:: Delivery Agent Name -->
        <template v-slot:[`item.user.name`]="{ item }">
          <p>{{ item.user?.name || "-" }}</p>
        </template>
        <!-- End:: Delivery Agent Name -->

        <!-- Start:: Requested Amount -->
        <template v-slot:[`item.balance`]="{ item }">
          <p class="font-weight-bold">{{ item.balance }} {{ item.currency }}</p>
        </template>
        <!-- End:: Requested Amount -->

        <!-- Start:: Request Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <p>{{ item.created_at }}</p>
        </template>
        <!-- End:: Request Date -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <span
            class="status_badge"
            :class="{
              'text-warning': item.status === 'pending',
              'text-success':
                item.status === 'done' || item.status === 'accepted',
              'text-danger': item.status === 'rejected',
            }"
          >
            {{ item.status_translated }}
          </span>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Bank Name -->
        <template v-slot:[`item.bank_name`]="{ item }">
          <p>{{ item.bank_name || "-" }}</p>
        </template>
        <!-- End:: Bank Name -->

        <!-- Start:: Account Number -->
        <template v-slot:[`item.account_number`]="{ item }">
          <p dir="ltr">{{ item.account_number || "-" }}</p>
        </template>
        <!-- End:: Account Number -->

        <!-- Start:: IBAN -->
        <template v-slot:[`item.iban`]="{ item }">
          <p dir="ltr">{{ item.iban || "-" }}</p>
        </template>
        <!-- End:: IBAN -->

        <!-- Start:: Rejection Reason -->
        <template v-slot:[`item.rejection_reason`]="{ item }">
          <p>{{ item.notes || "-" }}</p>
        </template>
        <!-- End:: Rejection Reason -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions" v-if="item.status === 'pending'">
            <button
              class="btn_accept"
              @click="openAcceptModal(item)"
              v-if="$can('wallets submitrequest', 'wallets')"
            >
              <i class="fal fa-check"></i>
            </button>
            <button
              class="btn_reject"
              @click="openRejectModal(item)"
              v-if="$can('wallets submitrequest', 'wallets')"
            >
              <i class="fal fa-times"></i>
            </button>
          </div>
          <span v-else class="text-muted">-</span>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!--  =========== Start:: Pagination =========== -->
      <template>
        <div class="pagination_container text-center mt-3 mb-0">
          <v-pagination
            v-model="paginations.current_page"
            :length="paginations.last_page"
            :total-visible="6"
            @input="updateRouterQueryParam($event)"
            :prev-icon="
              getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
            "
            :next-icon="
              getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
            "
          ></v-pagination>
        </div>
      </template>
      <!--  =========== End:: Pagination =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Accept Modal -->
    <AcceptWalletRequestModal
      :requestData="selectedRequest"
      :showModal="showAcceptModal"
      @closeModal="showAcceptModal = false"
      @refreshData="getAllRequests"
    />
    <!-- End:: Accept Modal -->

    <!-- Start:: Reject Modal -->
    <RejectWalletRequestModal
      :requestData="selectedRequest"
      :showModal="showRejectModal"
      @closeModal="showRejectModal = false"
      @refreshData="getAllRequests"
    />
    <!-- End:: Reject Modal -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AcceptWalletRequestModal from "./AcceptWalletRequestModal.vue";
import RejectWalletRequestModal from "./RejectWalletRequestModal.vue";

export default {
  name: "DeliveryWalletRequests",

  components: {
    AcceptWalletRequestModal,
    RejectWalletRequestModal,
  },

  data() {
    return {
      // Start:: Loader Control Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        request_number: null,
        name: null,
        status: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("walletRequests.serialNumber"),
          value: "serialNumber",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.requestId"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.deliveryAgentName"),
          value: "user.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.requestedAmount"),
          value: "balance",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.requestDate"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.requestStatus"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.bankName"),
          value: "bank_account_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.accountNumber"),
          value: "bank_account_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.iban"),
          value: "bank_iban",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.rejectionReason"),
          value: "rejection_reason",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("walletRequests.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Status Options
      statusOptions: [
        {
          id: null,
          name: this.$t("walletRequests.allStatuses"),
        },
        {
          id: "pending",
          name: this.$t("walletRequests.pending"),
        },
        {
          id: "accepted",
          name: this.$t("walletRequests.done"),
        },
        {
          id: "rejected",
          name: this.$t("walletRequests.rejected"),
        },
      ],
      // End:: Status Options

      // Start:: Modal Data
      showAcceptModal: false,
      showRejectModal: false,
      selectedRequest: null,
      // End:: Modal Data
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.getAllRequests();
    },
    ["$route.query.id"](newValue) {
      this.paginations.current_page = 1;
      this.getAllRequests();
    },
  },

  methods: {
    // Start:: Get All Requests
    async getAllRequests() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "wallets/show-requests-wallet/client",
            params: {
              page: this.paginations.current_page,
              request_number: this.filterOptions.request_number
                ? Number(this.filterOptions.request_number)
                : null,
              name: this.filterOptions.name,
              status: this.filterOptions.status?.id,
            },
        });
        this.loading = false;
        this.tableRows = res.data.data;
        this.paginations.last_page = res.data.meta.last_page;
        this.paginations.items_per_page = res.data.meta.per_page;
        this.tableRows.forEach((item, index) => {
          item.serialNumber =
            (this.paginations.current_page - 1) *
              this.paginations.items_per_page +
            index +
            1;
        });
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get All Requests

    // Start:: Submit Filter Form
    submitFilterForm() {
      this.paginations.current_page = 1;
      this.getAllRequests();
    },
    // End:: Submit Filter Form

    // Start:: Reset Filter
    resetFilter() {
      this.filterOptions.request_number = null;
      this.filterOptions.name = null;
      this.filterOptions.status = null;
      this.paginations.current_page = 1;
      this.getAllRequests();
    },
    // End:: Reset Filter

    // Start:: Open Accept Modal
    openAcceptModal(request) {
      this.selectedRequest = request;
      this.showAcceptModal = true;
    },
    // End:: Open Accept Modal

    // Start:: Open Reject Modal
    openRejectModal(request) {
      this.selectedRequest = request;
      this.showRejectModal = true;
    },
    // End:: Open Reject Modal

    // Start:: Update Router Query Param
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    // End:: Update Router Query Param
  },

  created() {
    this.paginations.current_page = Number(this.$route.query.page) || 1;
    this.filterOptions.request_number = this.$route.query.id || null;
    this.getAllRequests();
  },
};
</script>

<style scoped lang="scss">
.status_badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 13px;
}

.btn_accept {
  background: #4caf50;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 5px;

  &:hover {
    background: #45a049;
  }
}

.btn_reject {
  background: #f44336;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #da190b;
  }
}
</style>
